import React, { useEffect, useId, useRef } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /**
   * Accessible name for a dialog that renders its own heading instead of
   * `title` (ignored when `title` is set: the title bar labels the dialog).
   */
  ariaLabel?: string;
  maxWidth?: string;
  children: React.ReactNode;
  className?: string;
  /**
   * `false` blocks every dismissal (Escape, backdrop click, the title bar's ×)
   * for states the user must wait out, such as a loading step. Default `true`.
   */
  dismissable?: boolean;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Open dialog panels in opening order. */
const openPanels: HTMLElement[] = [];

/**
 * The dialog that owns Escape and Tab: the most recently opened one that
 * doesn't contain another open dialog (a nested dialog mounts, and so
 * registers, before its parent).
 */
function topPanel(): HTMLElement | undefined {
  for (let i = openPanels.length - 1; i >= 0; i -= 1) {
    const panel = openPanels[i];
    if (panel && !panel.querySelector('[aria-modal="true"]')) return panel;
  }
  return undefined;
}

/**
 * Modal Component
 *
 * A reusable modal dialog with backdrop, centered content, and mobile bottom-sheet behavior.
 *
 * Features:
 * - `role="dialog"` + `aria-modal`, named by `title` (or `ariaLabel`)
 * - Focus moves into the dialog on open (an `autoFocus` child keeps it), Tab
 *   and Shift+Tab stay inside, and focus returns to the opener on close
 * - Escape and backdrop click close it, unless `dismissable={false}`
 * - With nested dialogs, only the innermost handles Escape and Tab
 * - Mobile bottom-sheet behavior (slides up from bottom)
 * - Customizable max width and additional className
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Edit Account"
 *   maxWidth="650px"
 * >
 *   <div className="p-6">Modal content here</div>
 * </Modal>
 * ```
 */
export function Modal({
  isOpen,
  onClose,
  title,
  ariaLabel,
  maxWidth = '650px',
  children,
  className = '',
  dismissable = true,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissableRef = useRef(dismissable);
  dismissableRef.current = dismissable;

  // The element to refocus on close. Read during render on the opening
  // render, before an autoFocus child takes focus in the commit.
  const returnFocusRef = useRef<Element | null>(null);
  const wasOpenRef = useRef(false);
  if (isOpen && !wasOpenRef.current && typeof document !== 'undefined') {
    returnFocusRef.current = document.activeElement;
  }
  wasOpenRef.current = isOpen;

  useEffect(() => {
    const panel = panelRef.current;
    if (!isOpen || !panel) return;
    openPanels.push(panel);

    // Move focus in unless a child (autoFocus) already has it.
    if (!panel.contains(document.activeElement)) {
      panel.focus();
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (!panel || topPanel() !== panel) return;

      if (event.key === 'Escape') {
        if (dismissableRef.current) onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!first || !last) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const outside = !panel.contains(active) || active === panel;
      if (event.shiftKey && (outside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = openPanels.indexOf(panel);
      if (index >= 0) openPanels.splice(index, 1);
      const returnTo = returnFocusRef.current;
      if (returnTo instanceof HTMLElement && returnTo.isConnected) {
        returnTo.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (dismissable && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[var(--overlay-black-50)] dark:bg-[var(--overlay-black-70)] flex items-center justify-center z-[1000] max-md:items-end max-md:pb-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom))]"
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        className={`bg-surface rounded-xl shadow-[0_20px_40px_var(--overlay-black-20)] dark:shadow-[0_20px_40px_var(--overlay-black-50)] w-full max-h-[90vh] flex flex-col overflow-hidden outline-none max-md:w-full max-md:max-w-full max-md:max-h-[calc(100vh-120px)] max-md:rounded-t-xl max-md:rounded-b-none max-md:m-0 ${className}`}
        style={{ maxWidth }}
      >
        {title && (
          <div className="flex items-center justify-between py-5 px-6 border-b border-border flex-shrink-0">
            <h2 id={titleId} className="m-0 text-lg font-semibold text-text">{title}</h2>
            {dismissable && (
              <button
                type="button"
                className="bg-transparent border-none text-2xl text-text-muted cursor-pointer p-0 leading-none transition-colors hover:text-text"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
